import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { MeetingListItem } from '@ai-meeting/shared'
import SidebarToggle from '@/components/SidebarToggle'
import BacklogConnectNudge from '@/components/BacklogConnectNudge'
import { fmt } from '@/constants/ui-locale'
import {
  GOOGLE_CALENDAR_ENABLED,
  MEETING_BRIEF_ENABLED,
  UPCOMING_MEETINGS_ENABLED,
} from '@/constants/feature-flags'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchMeetingList } from '@/services/meetings'
import { useStore } from '@/store/useStore'
import type { PendingSavedMeeting, SaveToast as SaveToastState } from '@/store/useStore'
import { useJoinMeetingInvite } from '@/hooks/useJoinMeetingInvite'
import { useMeetingInvites } from '@/hooks/useMeetingInvites'
import LanguagePair from './components/LanguagePair'
import { getLanguageLabel } from './components/languageLabel'
import OngoingMeetingRow from './components/OngoingMeetingRow'
import { formatDate } from '@/utils/formatDateTime'
import UpcomingCalendar from './components/UpcomingCalendar'
import { readOwnedMeetingCount } from '@/utils/resolveDefaultMeetingName'

/**
 * The name to greet this reader by.
 *
 * The whole display name, not a part of it. Taking the first word greeted a
 * Vietnamese reader by their family name — `Đặng Thông` came out as "Hello,
 * Đặng!" — and taking the last would be worse: three of the six accounts in
 * this workspace are `Hang DT`, `Huyen TT`, `Tinh PX`, which put the given
 * name first and abbreviate the rest, so the last word is initials. No split
 * is right for both shapes. The Japanese greeting appends さん, where a family
 * name is the correct form anyway, so a split would have had to differ by
 * locale as well.
 *
 * A trailing parenthetical goes: display names here carry a team tag,
 * `Dang Dinh Thong (R1.React)`, which is not part of anybody's name.
 *
 * @param displayName The signed-in reader's stored name.
 * @returns What to put in the greeting; `there` when nothing is left of it.
 */
function getGreetingName(displayName: string): string {
  return displayName.replace(/\s*\([^)]*\)\s*$/, '').trim() || 'there'
}

/**
 * True for a session the user started and walked away from.
 *
 * Marked with a badge rather than hidden: the owner keeps seeing it, labelled
 * for what it is (BRAI-413). The row used to be dressed as an upcoming meeting
 * instead, which told the user the opposite of what is true (BRAI-446).
 */
function isUnfinishedSession(meeting: MeetingListItem): boolean {
  return meeting.completed === false
}

function MeetingGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 2h6l4 4v16H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v5h5M9 13h6M9 17h6" />
    </svg>
  )
}

function MeetingRow({
  meeting,
  locale,
  t,
}: {
  meeting: MeetingListItem
  locale: string
  t: ReturnType<typeof useUiStrings>['t']
}) {
  const isUnfinished = isUnfinishedSession(meeting)

  return (
    <Link
      to={`/meetings/${meeting.id}`}
      state={{ from: '/home' }}
      className="grid min-h-[76px] grid-cols-[auto_1fr] items-center gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3 shadow-sm transition-colors hover:border-[var(--accent)] sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:gap-5 sm:px-5"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-blue-100 text-[var(--accent)]">
        <MeetingGlyph />
      </div>

      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="truncate text-sm font-semibold text-[var(--text-primary)]">
            {meeting.title}
          </h3>
          {isUnfinished && (
            <span className="inline-flex shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
              {t.meetingUnfinishedBadge}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
          {formatDate(meeting.updatedAt, locale)}
          {meeting.durationMinutes > 0 && (
            <>
              <span className="mx-2">•</span>
              {meeting.durationMinutes} {t.mins}
            </>
          )}
        </p>
      </div>

      <div className="col-start-2 row-start-2 sm:col-auto sm:row-auto">
        <LanguagePair targetLang={meeting.targetLang} />
      </div>
    </Link>
  )
}

function PendingMeetingRow({
  meeting,
  t,
}: {
  meeting: PendingSavedMeeting
  t: ReturnType<typeof useUiStrings>['t']
}) {
  return (
    <div
      aria-disabled="true"
      className="grid min-h-[76px] grid-cols-[auto_1fr] items-center gap-3 rounded-lg border border-blue-200 bg-blue-50/70 px-4 py-3 opacity-75 shadow-sm sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:gap-5 sm:px-5"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-blue-100 text-[var(--accent)]">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-[var(--accent)]" />
      </div>

      <div className="min-w-0">
        <h3 className="truncate text-sm font-semibold text-[var(--text-primary)]">
          {meeting.title}
        </h3>
        <p className="mt-0.5 text-xs font-semibold text-[var(--accent)]">{t.saving}</p>
      </div>

      <div className="col-start-2 row-start-2 sm:col-auto sm:row-auto">
        <div className="flex items-center gap-1.5 text-xs text-[var(--text-tertiary)]">
          <span className="rounded bg-[var(--accent)] px-1.5 py-0.5 text-[10px] font-bold text-white">
            {getLanguageLabel(meeting.sourceLang)}
          </span>
          <span aria-hidden="true">→</span>
          <span className="rounded bg-[var(--accent)] px-1.5 py-0.5 text-[10px] font-bold text-white">
            {getLanguageLabel(meeting.targetLang)}
          </span>
        </div>
      </div>

      <div className="col-start-2 row-start-3 justify-self-start sm:col-auto sm:row-auto sm:justify-self-end">
        <span className="inline-flex rounded border border-blue-100 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700">
          {t.saving}
        </span>
      </div>
    </div>
  )
}

function SaveToastBanner({ toast }: { toast: SaveToastState }) {
  const tone =
    toast.kind === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-red-200 bg-red-50 text-red-700'

  return (
    <div
      role="status"
      className={`fixed right-4 top-4 z-50 max-w-sm rounded-xl border px-4 py-3 text-sm font-semibold shadow-lg ${tone}`}
    >
      {toast.message}
    </div>
  )
}

function EmptyState() {
  const { t } = useUiStrings()

  return (
    <div className="rounded-lg border border-dashed border-[var(--border-hover)] bg-[var(--bg-secondary)] px-6 py-12 text-center">
      <p className="text-sm font-semibold text-[var(--text-primary)]">{t.noMeetingsYet}</p>
      <p className="mt-1 text-xs text-[var(--text-secondary)]">{t.noMeetingsHint}</p>
    </div>
  )
}

export default function HomePage() {
  const { t, locale } = useUiStrings()
  const user = useStore((s) => s.user)
  const savedMeetingCount = useStore((s) => s.savedMeetingCount)
  const setSavedMeetingCount = useStore((s) => s.setSavedMeetingCount)
  const pendingSavedMeetings = useStore((s) => s.pendingSavedMeetings)
  const saveToast = useStore((s) => s.saveToast)
  const clearSaveToast = useStore((s) => s.clearSaveToast)
  // A second subscriber to the invite socket: `MeetingInviteBanner` is mounted app-wide and holds
  // its own. Correct — the gateway rooms each connection by user and replays to each — but it is
  // one connection more than the user needs, and the fix is to lift the subscription into a
  // provider both read from. Left for its own change rather than folded into this one.
  const { ongoing, dismiss, confirmStillLive } = useMeetingInvites()
  const { join, checkingId, endedIds, clearEnded } = useJoinMeetingInvite(confirmStillLive)
  const ongoingSharedAtRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    for (const inv of ongoing) {
      const prev = ongoingSharedAtRef.current.get(inv.meetingId)
      if (prev !== undefined && prev !== inv.sharedAt) {
        clearEnded(inv.meetingId)
      }
      ongoingSharedAtRef.current.set(inv.meetingId, inv.sharedAt)
    }
  }, [clearEnded, ongoing])
  const [recentMeetings, setRecentMeetings] = useState<MeetingListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const saveSuccessToastId = saveToast?.kind === 'success' ? saveToast.id : null

  useEffect(() => {
    let ignore = false

    async function loadMeetings() {
      try {
        setLoading(true)
        setError(null)
        // Calendar events load independently and are not BrSE recordings.
        const recentData = await fetchMeetingList({
          limit: 3,
          status: 'recent',
          sortBy: 'updatedAt',
          sortOrder: 'desc',
        })
        if (!ignore) {
          setRecentMeetings(recentData.items)
          setSavedMeetingCount(readOwnedMeetingCount(recentData, savedMeetingCount))
        }
      } catch (err) {
        if (!ignore) setError(err instanceof Error ? err.message : t.failedLoadMeetings)
      } finally {
        if (!ignore) setLoading(false)
      }
    }

    loadMeetings()
    return () => {
      ignore = true
    }
  }, [saveSuccessToastId, setSavedMeetingCount, t.failedLoadMeetings])

  useEffect(() => {
    if (!saveToast) return undefined

    const timeoutId = window.setTimeout(() => {
      clearSaveToast(saveToast.id)
    }, 4000)

    return () => window.clearTimeout(timeoutId)
  }, [clearSaveToast, saveToast])

  const greetingName = getGreetingName(user?.displayName ?? 'there')

  return (
    <>
      {saveToast && <SaveToastBanner toast={saveToast} />}
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 sm:px-6">
        <SidebarToggle />
        <h1 className="min-w-0 flex-1 truncate text-lg font-bold">{t.myMeetings}</h1>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
        {loading ? (
          <HomeLoadingState />
        ) : (
          <>
            {/* One-time notice. Renders nothing once skipped, once a Backlog
                account is connected, or for a space that does not use Backlog. */}
            <BacklogConnectNudge />
            <section className="rounded-lg border border-blue-200 bg-blue-50/70 px-5 py-5 shadow-sm sm:px-6 dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  {/* The greeting alone. The line under it used to be
                      `teamsCalendarHint`, word for word the sentence the
                      Teams Meetings section below prints — the same claim
                      twice on one screen, and neither time about the reader
                      being greeted. */}
                  <h2 className="text-lg font-bold">{fmt(t.hello, { name: greetingName })}</h2>
                </div>
                <CalendarLargeIcon className="hidden h-9 w-9 shrink-0 text-blue-300 sm:block dark:text-[var(--text-primary)]" />
              </div>
            </section>

            {/* Above Recent Meetings because it is the only part of this screen with something
                happening in it right now; a room nobody joins is a room that ends (BRAI-533).
                Rendered only when there is one — a section announcing that nothing is running is
                a line of chrome on every other day. */}
            {ongoing.length > 0 && (
              <section className="mt-8">
                <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                  {t.ongoingMeetings}
                </h2>
                <div className="mt-4 space-y-3">
                  {ongoing.map((invite) => (
                    <OngoingMeetingRow
                      key={invite.meetingId}
                      invite={invite}
                      isChecking={checkingId === invite.meetingId}
                      hasEnded={endedIds.includes(invite.meetingId)}
                      // Dismissed on the way in: the reader is acting on this invite, so the
                      // notification saying it arrived has nothing left to tell them.
                      onJoin={() => void join(invite, () => dismiss(invite.meetingId))}
                      t={t}
                    />
                  ))}
                </div>
              </section>
            )}

            <div className="mt-8 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                {t.recentMeetings}
              </h2>
              <Link to="/history" className="text-xs font-semibold text-[var(--accent)]">
                {t.viewAll}
              </Link>
            </div>

            <div className="mt-4 space-y-3">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs font-medium text-red-700">
                  {error}
                </div>
              )}
              {!error && pendingSavedMeetings.length === 0 && recentMeetings.length === 0 && (
                <EmptyState />
              )}
              {!error &&
                pendingSavedMeetings.map((meeting) => (
                  <PendingMeetingRow key={meeting.clientId} meeting={meeting} t={t} />
                ))}
              {!error &&
                recentMeetings.map((meeting) => (
                  <MeetingRow
                    key={meeting.id}
                    meeting={meeting}
                    locale={locale}
                    t={t}
                  />
                ))}
            </div>
          </>
        )}
        {/* One panel per provider (BRAI-538). Each connects on its own, so which ones
            are useful is the account's business, not the sign-in method's — Google is
            held back only while its consent screen is in Testing, not per account.
            Brief is release-gated independently from the calendar listing. */}
        {UPCOMING_MEETINGS_ENABLED && (
          <>
            <UpcomingCalendar provider="microsoft" withBrief={MEETING_BRIEF_ENABLED} />
            {GOOGLE_CALENDAR_ENABLED && (
              <UpcomingCalendar provider="google" withBrief={MEETING_BRIEF_ENABLED} />
            )}
          </>
        )}
      </div>
    </>
  )
}

function HomeLoadingState() {
  return (
    <div aria-busy="true" aria-live="polite">
      <section className="rounded-lg border border-blue-100 bg-blue-50/50 px-5 py-5 shadow-sm sm:px-6 dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]">
        <div className="animate-pulse space-y-3">
          <div className="h-5 w-48 rounded bg-blue-100 dark:bg-[var(--bg-tertiary)]" />
          <div className="h-4 w-full max-w-md rounded bg-blue-100/80 dark:bg-[var(--bg-tertiary)]" />
        </div>
      </section>

      <div className="mt-8 h-4 w-36 animate-pulse rounded bg-[var(--bg-tertiary)]" />
      <div className="mt-4 space-y-3">
        <LoadingRows />
      </div>
    </div>
  )
}

function LoadingRows({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="h-[76px] animate-pulse rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]"
        />
      ))}
    </>
  )
}

function CalendarLargeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 3v4m10-4v4M5 8h14M6 5h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
      />
    </svg>
  )
}
